import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { TimezonesList } from '@/components/TimezoneList';
import { 
	Settings, Users, Eye, Target, Filter as FilterIcon, 
	List, Plug, Download, Shield, Globe, MapPin, 
	FileText, Mail, AlertTriangle, ChevronLeft, Search,
	Edit2, Trash2, ChevronDown, Plus, Copy, Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ConfirmProvider';
import GoalModal from '@/components/GoalModal';
import FunnelModal from '@/components/FunnelModal';
import HeaderBar from '@/components/HeaderBar';
import ImportExportSection from '@/components/ImportExportSection';
import IntegrationsSection from '@/components/IntegrationsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = ({ user, token, logout }) => {
	const confirm = useConfirm();
	const { websiteId, tab } = useParams();
	const navigate = useNavigate();
	const [website, setWebsite] = useState(null);
	const [loading, setLoading] = useState(true);
	const [accessInfo, setAccessInfo] = useState(null);
	const activeTab = tab || 'general';
	
	// Form states
	const [domain, setDomain] = useState('');
	const [timezone1, setTimezone1] = useState('');
	const [selectedtimezone, setSelectedTimezone] = useState(null);
	// Visibility states
	const [isPublic, setIsPublic] = useState(false);
	const [sanitizeUrls, setSanitizeUrls] = useState(false);
	const [sanitizeLoading, setSanitizeLoading] = useState(false);
	const [publicUrl, setPublicUrl] = useState('');
	const [sharedLinks, setSharedLinks] = useState([]);
	const [showAddLinkModal, setShowAddLinkModal] = useState(false);
	const [newLinkName, setNewLinkName] = useState('');
	const [newLinkPassword, setNewLinkPassword] = useState('');
	const [visibilityLoading, setVisibilityLoading] = useState(false);
	
	// Goals states
	const [goals, setGoals] = useState([]);
	const [showInDashboard, setShowInDashboard] = useState(true);
	const [goalSearchQuery, setGoalSearchQuery] = useState('');
	const [showAddGoalDropdown, setShowAddGoalDropdown] = useState(false);
	const [showGoalModal, setShowGoalModal] = useState(false);
	const [selectedGoalType, setSelectedGoalType] = useState(null);
	const [editingGoal, setEditingGoal] = useState(null);

	// Funnels states
	const [funnels, setFunnels] = useState([]);
	const [funnelShowInDashboard, setFunnelShowInDashboard] = useState(true);
	const [showFunnelModal, setShowFunnelModal] = useState(false);
	const [editingFunnel, setEditingFunnel] = useState(null);

	// Danger Zone states
	const [transferEmail, setTransferEmail] = useState('');
	const [showTransferModal, setShowTransferModal] = useState(false);
	const [showResetModal, setShowResetModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [confirmText, setConfirmText] = useState('');
	const [dangerLoading, setDangerLoading] = useState(false);

	// Email Reports states
	const [emailSettings, setEmailSettings] = useState({
		weekly_report: { enabled: false, recipients: [] },
		monthly_report: { enabled: false, recipients: [] },
		traffic_spike: { enabled: false, threshold: 10, recipients: [] },
		traffic_drop: { enabled: false, threshold: 1, recipients: [] }
	});
	const [newRecipient, setNewRecipient] = useState({
		weekly: '',
		monthly: '',
		spike: '',
		drop: ''
	});
	const [spikeThreshold, setSpikeThreshold] = useState(10);
	const [dropThreshold, setDropThreshold] = useState(1);

	// Blocking settings states
	const [blockSettings, setBlockSettings] = useState({
		blocked_ips: [],
		blocked_countries: [],
		blocked_pages: []
	});
	const [newBlockItem, setNewBlockItem] = useState({
		ip: '',
		country: '',
		page: ''
	});
	
	// Modal states for blocking
	const [showIPModal, setShowIPModal] = useState(false);
	const [showCountryModal, setShowCountryModal] = useState(false);
	const [showPageModal, setShowPageModal] = useState(false);
	const [shieldsExpanded, setShieldsExpanded] = useState(true);
	const [availablePages, setAvailablePages] = useState([]);
	
	// Custom Properties states
	const [customProperties, setCustomProperties] = useState([]);
	const [showPropertyModal, setShowPropertyModal] = useState(false);
	const [newPropertyName, setNewPropertyName] = useState('');
	const [propertySearchQuery, setPropertySearchQuery] = useState('');
	const [propertyShowInDashboard, setPropertyShowInDashboard] = useState(true);
	
	// Country list for dropdown
	const countries = [
		{ code: 'US', name: 'United States' },
		{ code: 'GB', name: 'United Kingdom' },
		{ code: 'CA', name: 'Canada' },
		{ code: 'AU', name: 'Australia' },
		{ code: 'DE', name: 'Germany' },
		{ code: 'FR', name: 'France' },
		{ code: 'IT', name: 'Italy' },
		{ code: 'ES', name: 'Spain' },
		{ code: 'NL', name: 'Netherlands' },
		{ code: 'SE', name: 'Sweden' },
		{ code: 'NO', name: 'Norway' },
		{ code: 'DK', name: 'Denmark' },
		{ code: 'FI', name: 'Finland' },
		{ code: 'BE', name: 'Belgium' },
		{ code: 'CH', name: 'Switzerland' },
		{ code: 'AT', name: 'Austria' },
		{ code: 'PL', name: 'Poland' },
		{ code: 'RO', name: 'Romania' },
		{ code: 'CZ', name: 'Czech Republic' },
		{ code: 'GR', name: 'Greece' },
		{ code: 'PT', name: 'Portugal' },
		{ code: 'HU', name: 'Hungary' },
		{ code: 'IE', name: 'Ireland' },
		{ code: 'IN', name: 'India' },
		{ code: 'CN', name: 'China' },
		{ code: 'JP', name: 'Japan' },
		{ code: 'KR', name: 'South Korea' },
		{ code: 'SG', name: 'Singapore' },
		{ code: 'MY', name: 'Malaysia' },
		{ code: 'TH', name: 'Thailand' },
		{ code: 'ID', name: 'Indonesia' },
		{ code: 'PH', name: 'Philippines' },
		{ code: 'VN', name: 'Vietnam' },
		{ code: 'BR', name: 'Brazil' },
		{ code: 'MX', name: 'Mexico' },
		{ code: 'AR', name: 'Argentina' },
		{ code: 'CL', name: 'Chile' },
		{ code: 'CO', name: 'Colombia' },
		{ code: 'ZA', name: 'South Africa' },
		{ code: 'EG', name: 'Egypt' },
		{ code: 'NG', name: 'Nigeria' },
		{ code: 'KE', name: 'Kenya' },
		{ code: 'RU', name: 'Russia' },
		{ code: 'UA', name: 'Ukraine' },
		{ code: 'TR', name: 'Turkey' },
		{ code: 'SA', name: 'Saudi Arabia' },
		{ code: 'AE', name: 'United Arab Emirates' },
		{ code: 'IL', name: 'Israel' },
		{ code: 'NZ', name: 'New Zealand' },
		{ code: 'PK', name: 'Pakistan' },
		{ code: 'BD', name: 'Bangladesh' }
	].sort((a, b) => a.name.localeCompare(b.name));

	useEffect(() => {
		checkAccessAndFetchWebsite();
	}, [websiteId]);

	useEffect(() => {
		if (!accessInfo) return; // Wait for access check
		
		if (activeTab === 'goals') {
			fetchGoals();
		}
		if (activeTab === 'funnels') {
			fetchFunnels();
		}
		if (activeTab === 'email') {
			fetchEmailSettings();
		}
		if (activeTab === 'ip' || activeTab === 'countries' || activeTab === 'pages') {
			fetchBlockSettings();
		}
		if (activeTab === 'pages') {
			fetchAvailablePages();
		}
		if (activeTab === 'properties') {
			fetchCustomProperties();
		}
		if (activeTab === 'visibility') {
			fetchVisibilitySettings();
		}
	}, [websiteId, activeTab, accessInfo]);

	// Check user access before loading settings
	const checkAccessAndFetchWebsite = async () => {
		try {
			// First check access permissions
			const accessRes = await axios.get(`${API}/websites/${websiteId}/access`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			
			const access = accessRes.data;
			setAccessInfo(access);
			
			// If user is viewer or billing (no 'settings' permission), redirect
			if (!access.permissions.includes('settings')) {
				toast.error(`Access denied. Your role (${access.role}) doesn't have permission to access settings.`);
				navigate(`/analytics/${websiteId}`);
				return;
			}
			
			// Access granted, fetch website details
			fetchWebsite();
		} catch (err) {
			if (err.response?.status === 403) {
				toast.error('You do not have access to this website');
				navigate('/');
			} else if (err.response?.status === 404) {
				toast.error('Website not found');
				navigate('/');
			} else {
				toast.error('Failed to verify access');
				setLoading(false);
			}
		}
	};
	
	// Fetch visibility settings
	const fetchVisibilitySettings = async () => {
		try {
			const [visRes, linksRes] = await Promise.all([
				axios.get(`${API}/websites/${websiteId}/visibility`, {
					headers: { Authorization: `Bearer ${token}` }
				}),
				axios.get(`${API}/websites/${websiteId}/shared-links`, {
					headers: { Authorization: `Bearer ${token}` }
				})
			]);
			setIsPublic(visRes.data.is_public || false);
			setSanitizeUrls(visRes.data.sanitize_public_urls || false);
			setPublicUrl(visRes.data.public_url || '');
			setSharedLinks(linksRes.data || []);
		} catch (err) {
			console.error('Error fetching visibility settings:', err);
		}
	};
	
	// Toggle public visibility
	const togglePublicVisibility = async () => {
		setVisibilityLoading(true);
		try {
			const newValue = !isPublic;
			await axios.put(
				`${API}/websites/${websiteId}/visibility?is_public=${newValue}`,
				{},
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			setIsPublic(newValue);
			toast.success(newValue ? 'Dashboard is now public' : 'Dashboard is now private');
		} catch (err) {
			toast.error('Failed to update visibility');
		} finally {
			setVisibilityLoading(false);
		}
	};

	// Toggle URL sanitization for the public view. Sends the CURRENT is_public
	// alongside so the PUT doesn't accidentally flip visibility.
	const toggleSanitizeUrls = async () => {
		setSanitizeLoading(true);
		try {
			const newValue = !sanitizeUrls;
			await axios.put(
				`${API}/websites/${websiteId}/visibility?is_public=${isPublic}&sanitize_public_urls=${newValue}`,
				{},
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			setSanitizeUrls(newValue);
			toast.success(newValue ? 'Sensitive URLs will be hidden in public view' : 'Public view now shows full URLs');
		} catch (err) {
			toast.error('Failed to update URL sanitization');
		} finally {
			setSanitizeLoading(false);
		}
	};
	
	// Create shared link
	const createSharedLink = async () => {
		if (!newLinkName.trim()) {
			toast.error('Please enter a name for the link');
			return;
		}
		try {
			const res = await axios.post(
				`${API}/websites/${websiteId}/shared-links`,
				{ name: newLinkName.trim(), password: newLinkPassword || null },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			setSharedLinks([...sharedLinks, res.data]);
			setShowAddLinkModal(false);
			setNewLinkName('');
			setNewLinkPassword('');
			toast.success('Shared link created');
		} catch (err) {
			toast.error('Failed to create shared link');
		}
	};
	
	// Delete shared link
	const deleteSharedLink = async (linkId) => {
		try {
			await axios.delete(
				`${API}/websites/${websiteId}/shared-links/${linkId}`,
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			setSharedLinks(sharedLinks.filter(l => l.id !== linkId));
			toast.success('Shared link deleted');
		} catch (err) {
			toast.error('Failed to delete shared link');
		}
	};
	
	// Fetch available pages for dropdown
	const fetchAvailablePages = async () => {
		// Default common pages
		const defaultPages = [
			'/',
			'/about',
			'/contact',
			'/blog',
			'/services',
			'/pricing',
			'/faq',
			'/terms',
			'/privacy',
			'/dashboard',
			'/admin',
			'/login',
			'/signup',
			'/profile'
		];
		
		try {
			const res = await axios.get(`${API}/analytics/${websiteId}/pages-stats`, {
				headers: { Authorization: `Bearer ${token}` },
				params: { period: '30d' }
			});
			if (res.data && res.data.top_pages && res.data.top_pages.length > 0) {
				// Use actual tracked pages from database
				const trackedPages = res.data.top_pages.map(p => p.path);
				// Combine with default pages and remove duplicates
				const allPages = [...new Set([...defaultPages, ...trackedPages])];
				setAvailablePages(allPages.sort());
			} else {
				// Use only default pages if no tracked pages
				setAvailablePages(defaultPages.sort());
			}
		} catch (err) {
			console.error('Failed to load pages:', err);
			// Use default pages on error
			setAvailablePages(defaultPages.sort());
		}
	};

	const fetchFunnels = async () => {
		try {
			const res = await axios.get(`${API}/funnels/${websiteId}`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			setFunnels(res.data || []);
		} catch (err) {
			console.error('Failed to load funnels:', err);
			toast.error('Failed to load funnels');
		}
	};

	const handleDeleteFunnel = async (funnelId) => {
		const ok = await confirm({
			title: 'Delete this funnel?',
			description: 'The funnel definition and its conversion history will be removed. This cannot be undone.',
			confirmLabel: 'Delete funnel',
		});
		if (!ok) return;

		try {
			await axios.delete(`${API}/funnels/${funnelId}`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			toast.success('Funnel deleted successfully');
			fetchFunnels();
		} catch (err) {
			toast.error('Failed to delete funnel');
		}
	};

	const handleEditFunnel = (funnel) => {
		setEditingFunnel(funnel);
		setShowFunnelModal(true);
	};

	const handleFunnelSuccess = () => {
		fetchFunnels();
		setEditingFunnel(null);
	};

	// Danger Zone handlers
	const handleTransferOwnership = async () => {
		if (!transferEmail) {
			toast.error('Please enter an email address');
			return;
		}
		
		setDangerLoading(true);
		try {
			await axios.post(
				`${API}/websites/${websiteId}/transfer-ownership`,
				{ new_owner_email: transferEmail },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			toast.success('Website ownership transferred successfully');
			setShowTransferModal(false);
			setTransferEmail('');
			navigate('/');
		} catch (err) {
			toast.error(err.response?.data?.detail || 'Failed to transfer ownership');
		}
		setDangerLoading(false);
	};

	const handleResetStats = async () => {
		if (confirmText !== website?.domain) {
			toast.error('Please type the domain name to confirm');
			return;
		}
		
		setDangerLoading(true);
		try {
			await axios.post(
				`${API}/websites/${websiteId}/reset-stats`,
				{},
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			toast.success('Statistics reset successfully');
			setShowResetModal(false);
			setConfirmText('');
		} catch (err) {
			toast.error(err.response?.data?.detail || 'Failed to reset statistics');
		}
		setDangerLoading(false);
	};

	const handleDeleteSite = async () => {
		if (confirmText !== website?.domain) {
			toast.error('Please type the domain name to confirm');
			return;
		}
		
		setDangerLoading(true);
		try {
			await axios.delete(`${API}/websites/${websiteId}`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			toast.success('Website deleted successfully');
			navigate('/');
		} catch (err) {
			toast.error(err.response?.data?.detail || 'Failed to delete website');
		}
		setDangerLoading(false);
	};

	// Email Settings Functions
	const fetchEmailSettings = async () => {
		try {
			const res = await axios.get(`${API}/email-settings/${websiteId}`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			setEmailSettings(res.data);
			setSpikeThreshold(res.data.traffic_spike?.threshold || 10);
			setDropThreshold(res.data.traffic_drop?.threshold || 1);
		} catch (err) {
			console.error('Failed to load email settings:', err);
		}
	};

	const updateEmailSettings = async (updatedSettings) => {
		try {
			await axios.put(
				`${API}/email-settings/${websiteId}`,
				updatedSettings,
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			setEmailSettings(updatedSettings);
			toast.success('Settings updated successfully');
		} catch (err) {
			toast.error('Failed to update settings');
		}
	};

	const toggleReport = async (reportType) => {
		const updated = {
			...emailSettings,
			[reportType]: {
				...emailSettings[reportType],
				enabled: !emailSettings[reportType].enabled
			}
		};
		await updateEmailSettings(updated);
	};

	const addRecipient = async (reportType) => {
		const email = newRecipient[reportType];
		if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			toast.error('Please enter a valid email address');
			return;
		}

		try {
			await axios.post(
				`${API}/email-settings/${websiteId}/add-recipient`,
				{ email, report_type: reportType },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			
			// Update local state
			const updated = {
				...emailSettings,
				[reportType === 'spike' || reportType === 'drop' ? `traffic_${reportType}` : `${reportType}_report`]: {
					...emailSettings[reportType === 'spike' || reportType === 'drop' ? `traffic_${reportType}` : `${reportType}_report`],
					recipients: [...emailSettings[reportType === 'spike' || reportType === 'drop' ? `traffic_${reportType}` : `${reportType}_report`].recipients, email]
				}
			};
			setEmailSettings(updated);
			setNewRecipient({ ...newRecipient, [reportType]: '' });
			toast.success('Recipient added');
		} catch (err) {
			toast.error(err.response?.data?.detail || 'Failed to add recipient');
		}
	};

	const removeRecipient = async (reportType, email) => {
		try {
			await axios.delete(
				`${API}/email-settings/${websiteId}/remove-recipient`,
				{
					headers: { Authorization: `Bearer ${token}` },
					params: { email, report_type: reportType }
				}
			);
			
			// Update local state
			const fieldName = reportType === 'spike' || reportType === 'drop' ? `traffic_${reportType}` : `${reportType}_report`;
			const updated = {
				...emailSettings,
				[fieldName]: {
					...emailSettings[fieldName],
					recipients: emailSettings[fieldName].recipients.filter(r => r !== email)
				}
			};
			setEmailSettings(updated);
			toast.success('Recipient removed');
		} catch (err) {
			toast.error('Failed to remove recipient');
		}
	};

	const saveThreshold = async (type) => {
		const updated = {
			...emailSettings,
			[type === 'spike' ? 'traffic_spike' : 'traffic_drop']: {
				...emailSettings[type === 'spike' ? 'traffic_spike' : 'traffic_drop'],
				threshold: type === 'spike' ? spikeThreshold : dropThreshold
			}
		};
		await updateEmailSettings(updated);
	};

	const fetchBlockSettings = async () => {
		try {
			const res = await axios.get(`${API}/websites/${websiteId}/blocks`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			setBlockSettings(res.data || {
				blocked_ips: [],
				blocked_countries: [],
				blocked_pages: []
			});
		} catch (err) {
			console.error('Failed to load block settings:', err);
			// Initialize with empty arrays if fetch fails
			setBlockSettings({
				blocked_ips: [],
				blocked_countries: [],
				blocked_pages: []
			});
		}
	};

	const addBlockItem = async (blockType) => {
		const item = newBlockItem[blockType];
		if (!item || !item.trim()) {
			toast.error(`Please select a ${blockType === 'ip' ? 'IP address' : blockType === 'country' ? 'country' : 'page'}`);
			return;
		}

		// Validation only for IP (country and page are selected from dropdown)
		if (blockType === 'ip') {
			// Basic IP validation (IPv4)
			const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
			if (!ipRegex.test(item.trim())) {
				toast.error('Please enter a valid IPv4 address');
				return;
			}
		}

		try {
			await axios.post(
				`${API}/websites/${websiteId}/blocks/add`,
				{ item: item.trim(), block_type: blockType },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			
			// Update local state
			const fieldMap = {
				ip: 'blocked_ips',
				country: 'blocked_countries',
				page: 'blocked_pages'
			};
			const field = fieldMap[blockType];
			setBlockSettings(prev => ({
				...prev,
				[field]: [...prev[field], item.trim()]
			}));
			setNewBlockItem({ ...newBlockItem, [blockType]: '' });
			toast.success(`${blockType === 'ip' ? 'IP address' : blockType === 'country' ? 'Country' : 'Page'} blocked successfully`);
		} catch (err) {
			toast.error(err.response?.data?.detail || `Failed to add ${blockType} to block list`);
		}
	};

	const removeBlockItem = async (blockType, item) => {
		try {
			await axios.delete(
				`${API}/websites/${websiteId}/blocks/remove`,
				{
					headers: { Authorization: `Bearer ${token}` },
					params: { item, block_type: blockType }
				}
			);
			
			// Update local state
			const fieldMap = {
				ip: 'blocked_ips',
				country: 'blocked_countries',
				page: 'blocked_pages'
			};
			const field = fieldMap[blockType];
			setBlockSettings(prev => ({
				...prev,
				[field]: prev[field].filter(i => i !== item)
			}));
			toast.success(`${blockType === 'ip' ? 'IP address' : blockType === 'country' ? 'Country' : 'Page'} removed from block list`);
		} catch (err) {
			toast.error(`Failed to remove ${blockType}`);
		}
	};

	// Custom Properties functions
	const fetchCustomProperties = async () => {
		try {
			const res = await axios.get(`${API}/custom-properties/${websiteId}`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			setCustomProperties(res.data || []);
		} catch (err) {
			console.error('Failed to load custom properties:', err);
			setCustomProperties([]);
		}
	};

	const addCustomProperty = async () => {
		if (!newPropertyName.trim()) {
			toast.error('Please enter a property name');
			return;
		}

		try {
			const res = await axios.post(
				`${API}/custom-properties/${websiteId}`,
				{ name: newPropertyName.trim() },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			setCustomProperties(prev => [...prev, res.data]);
			setNewPropertyName('');
			setShowPropertyModal(false);
			toast.success('Property added successfully');
		} catch (err) {
			toast.error(err.response?.data?.detail || 'Failed to add property');
		}
	};

	const deleteCustomProperty = async (propertyId) => {
		try {
			await axios.delete(`${API}/custom-properties/${propertyId}`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			setCustomProperties(prev => prev.filter(p => p.id !== propertyId));
			toast.success('Property deleted successfully');
		} catch (err) {
			toast.error('Failed to delete property');
		}
	};

	const filteredProperties = customProperties.filter(prop =>
		prop.name.toLowerCase().includes(propertySearchQuery.toLowerCase())
	);

	const fetchWebsite = async () => {
		try {
			const res = await axios.get(`${API}/websites`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			const site = res.data.find(w => w.id === websiteId);
			if (site) {
				setWebsite(site);
				setDomain(site.domain);



				const selected = TimezonesList.find(
					(tz) => tz.value === site.timezone
				);
				setTimezone1(selected || null);



				//setTimezone(site.timezone || 'America/Phoenix');
			}
			setLoading(false);
		} catch (err) {
			toast.error('Failed to load website');
			setLoading(false);
		}
	};

	const handleChangeDomain = async (e) => {
		e.preventDefault();
		// Light client-side cleanup so the UX matches what the backend will store.
		// Server does the authoritative normalization + uniqueness check.
		const cleaned = (domain || '').trim();
		if (!cleaned) {
			toast.error('Domain is required');
			return;
		}
		try {
			const res = await axios.put(
				`${API}/websites/${websiteId}/domain`,
				null,
				{
					headers: { Authorization: `Bearer ${token}` },
					params: { domain: cleaned },
				}
			);
			// Sync the input + the website state with the normalized value the
			// server actually stored (e.g. stripped https:// or trailing slash).
			if (res.data?.domain) setDomain(res.data.domain);
			toast.success('Domain updated. Your tracking + history are unchanged.');
			fetchWebsite();
		} catch (err) {
			const detail = err.response?.data?.detail;
			toast.error(typeof detail === 'string' ? detail : 'Failed to update domain');
		}
	};

	const handleSaveTimezone = async (e) => {

		e.preventDefault();
		var timezone = timezone1.value; // ✅ ONLY string
		try {
			await axios.put(
				`${API}/websites/${websiteId}`,
				{ timezone },
				{
					headers: { Authorization: `Bearer ${token}` },
					params: { timezone }
				}
			);
			toast.success('Timezone updated successfully');
			fetchWebsite();
		} catch (err) {
			toast.error(err.response?.data?.detail || 'Failed to update timezone');
		}
	};

	const fetchGoals = async () => {
		try {
			const res = await axios.get(`${API}/goals/${websiteId}`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			
			// If no goals exist, create default ones
			if (!res.data || res.data.length === 0) {
				await createDefaultGoals();
			} else {
				setGoals(res.data);
			}
		} catch (err) {
			console.error('Failed to load goals:', err);
			// If error fetching goals, try creating default ones
			await createDefaultGoals();
		}
	};

	const createDefaultGoals = async () => {
		const defaultGoals = [
			{ name: 'Form: Submission', goal_type: 'custom_event', page_path: '/form-submit' },
			{ name: 'File Download', goal_type: 'custom_event', page_path: '/download' },
			{ name: 'Outbound Link: Click', goal_type: 'custom_event', page_path: '/outbound' },
			{ name: '404', goal_type: 'custom_event', page_path: '/404' }
		];

		try {
			const createdGoals = [];
			for (const goal of defaultGoals) {
				const response = await axios.post(
					`${API}/goals`,
					{ website_id: websiteId, ...goal },
					{ headers: { Authorization: `Bearer ${token}` } }
				);
				createdGoals.push(response.data);
			}
			setGoals(createdGoals);
		} catch (err) {
			console.error('Failed to create default goals:', err);
		}
	};

	const handleAddGoal = (goalType) => {
		setShowAddGoalDropdown(false);
		setSelectedGoalType(goalType);
		setShowGoalModal(true);
	};

	const options = [
		{ value: 'react', label: 'React' },
		{ value: 'vue', label: 'Vue' },
		{ value: 'angular', label: 'Angular' }
	];


	const handleGoalSuccess = () => {
		toast.success(editingGoal ? 'Goal updated successfully' : 'Goal added successfully');
		fetchGoals();
		setEditingGoal(null);
	};

	const handleEditGoal = (goal) => {
		setEditingGoal(goal);
		setSelectedGoalType(goal.goal_type);
		setShowGoalModal(true);
	};

	const handleDeleteGoal = async (goalId) => {
		const ok = await confirm({
			title: 'Delete this goal?',
			description: 'The goal will be removed and stop being tracked. Past conversions stay in your analytics history.',
			confirmLabel: 'Delete goal',
		});
		if (!ok) return;

		try {
			await axios.delete(`${API}/goals/${goalId}`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			
			// Update the goals state immediately by filtering out the deleted goal
			setGoals(prevGoals => prevGoals.filter(goal => goal.id !== goalId));
			toast.success('Goal deleted successfully');
		} catch (err) {
			toast.error('Failed to delete goal');
			console.error('Delete goal error:', err);
		}
	};

	const menuItems = [
		{ id: 'general', label: 'General', icon: Settings },
		{ id: 'people', label: 'People', icon: Users },
		{ id: 'visibility', label: 'Visibility', icon: Eye },
		{ id: 'goals', label: 'Goals', icon: Target },
		{ id: 'funnels', label: 'Funnels', icon: FilterIcon },
		{ id: 'properties', label: 'Custom properties', icon: List },
		{ id: 'integrations', label: 'Integrations', icon: Plug },
		{ id: 'imports', label: 'Imports & exports', icon: Download },
		{ 
			id: 'shields', 
			label: 'Shields', 
			icon: Shield,
			subItems: [
				{ id: 'ip', label: 'IP addresses' },
				{ id: 'countries', label: 'Countries' },
				{ id: 'pages', label: 'Pages' },
				
			]
		},
		{ id: 'email', label: 'Email reports', icon: Mail },
		{ id: 'danger', label: 'Danger zone', icon: AlertTriangle },
	];

	if (loading) {
		return <div className="loading">Loading...</div>;
	}

	if (!website) {
		return <div className="loading">Website not found</div>;
	}

	return (
		<div className="settings-page">
			{/* Header */}
			 <HeaderBar user={user} logout={logout} />
			<div className="settings-header">
				<div className="settings-header-content settings-header-row">
					<h1 className="settings-title">
						<span className="settings-title-label">Settings for</span>
						<span className="settings-title-domain">
							<span className="domain-dot" aria-hidden="true"></span>
							{website.domain?.replace(/^https?:\/\//, '').replace(/\/$/, '')}
						</span>
					</h1>
					<Button
						variant="outline"
						size="sm"
						onClick={() => navigate(`/analytics/${websiteId}`)}
						className="back-button"
					>
						<ChevronLeft className="w-4 h-4" />
						Back to stats
					</Button>
				</div>
			</div>

			<div className="settings-container">
				{/* Sidebar */}
				<div className="settings-sidebar">
					<nav className="settings-nav">
						{menuItems.map((item) => {
							const Icon = item.icon;
							
							// Check if this item has sub-items (Shields menu)
							if (item.subItems) {
								const isAnySubItemActive = item.subItems.some(subItem => activeTab === subItem.id);
								
								return (
									<div key={item.id}>
										<button
											onClick={() => setShieldsExpanded(!shieldsExpanded)}
											className={`settings-nav-item ${isAnySubItemActive ? 'active' : ''}`}
											style={{ justifyContent: 'space-between', width: '100%' }}
										>
											<div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
												<Icon className="nav-icon" />
												<span>{item.label}</span>
											</div>
											<ChevronDown 
												className="w-4 h-4" 
												style={{ 
													transform: shieldsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
													transition: 'transform 0.2s'
												}}
											/>
										</button>
										
										{shieldsExpanded && (
											<div style={{ paddingLeft: '2rem' }}>
												{item.subItems.map((subItem) => (
													<Link
														key={subItem.id}
														to={`/settings/${websiteId}/${subItem.id}`}
														className={`settings-nav-item ${activeTab === subItem.id ? 'active' : ''}`}
														style={{ fontSize: '0.813rem' }}
													>
														<span>{subItem.label}</span>
													</Link>
												))}
											</div>
										)}
									</div>
								);
							}
							
							// Regular menu item
							return (
								<Link
									key={item.id}
									to={`/settings/${websiteId}/${item.id}`}
									className={`settings-nav-item ${activeTab === item.id ? 'active' : ''}`}
									data-testid={`nav-${item.id}`}
								>
									<Icon className="nav-icon" /><span>{item.label}</span>
								</Link>
							);
						})}
					</nav>
				</div>

				{/* Main Content */}
				<div className="settings-main">
					{activeTab === 'general' && (
						<div className="settings-content">
							{/* Site Domain Section */}
							<div className="settings-section">
								<div className="section-header">
									<h2 className="section-title">Site domain</h2>
									<button className="info-button">
										<svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
									</button>
								</div>
								<p className="section-description">Moving your site to a different domain? We got you!</p>
								
								<form onSubmit={handleChangeDomain} className="section-form">
									<div className="form-group">
										<label className="form-label">Domain</label>
										<Input
											type="text"
											value={domain}
											onChange={(e) => setDomain(e.target.value)}
											className="form-input"
											placeholder="example.com"
										/>
									</div>
									<Button type="submit" className="form-button">
										Change domain
									</Button>
								</form>
							</div>

							{/* Site Timezone Section */}
							<div className="settings-section">
								<div className="section-header">
									<h2 className="section-title">Site timezone</h2>
									<button className="info-button">
										<svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
									</button>
								</div>
								<p className="section-description">Update your reporting timezone.</p>
								
								<form onSubmit={handleSaveTimezone} className="section-form">
									<div className="form-group">
										<label className="form-label">Reporting timezone</label>
										<Select
											defaultValue={{ value: 'UCT', label: '(GMT+00:00) UCT' }}
											options={TimezonesList}

											value={timezone1}
											onChange={(values) => setTimezone1(values) }
											placeholder="Select TimeZone"
										/>


									</div>
									<Button type="submit" className="form-button">
										Save timezone
									</Button>
								</form>
							</div>

							{/* Site Installation Section */}
							<div className="settings-section">
								<div className="section-header">
									<h2 className="section-title">Site installation</h2>
									<button className="info-button">
										<svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
									</button>
								</div>
								<p className="section-description">Control what data is collected and verify your installation.</p>
								
								<Button 
									onClick={() => navigate(`/analytics/${websiteId}`)}
									className="form-button"
								>
									Review installation
								</Button>
							</div>
						</div>
					)}

					{/* Goals Tab */}
					{activeTab === 'goals' && (
						<div className="settings-content">
							<div className="goals-section">
								{/* Header */}
								<div className="goals-header">
									<div className="goals-header-left">
										<h2 className="goals-title">Goals</h2>
										<button className="info-button">
											<svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										</button>
									</div>
								</div>

								{/* Description */}
								<p className="goals-description">
									Define actions that you want your users to take, like visiting a certain page, submitting a form, etc. You can also{' '}
									<a href="#" className="goals-link">compose goals into funnels</a>.
								</p>

								{/* Toggle */}
								<div className="goals-toggle">
									<label className="toggle-switch">
										<input
											type="checkbox"
											checked={showInDashboard}
											onChange={(e) => setShowInDashboard(e.target.checked)}
										/>
										<span className="toggle-slider"></span>
									</label>
									<span className="toggle-label">Show in dashboard</span>
								</div>

								{/* Search and Add Goal */}
								<div className="goals-actions">
									<div className="search-container">
										<Search className="search-icon" />
										<input
											type="text"
											placeholder="Press / to search"
											value={goalSearchQuery}
											onChange={(e) => setGoalSearchQuery(e.target.value)}
											className="search-input"
										/>
									</div>

									<div className="add-goal-dropdown-container">
										<button
											onClick={() => setShowAddGoalDropdown(!showAddGoalDropdown)}
											className="add-goal-button"
										>
											Add goal
											<ChevronDown className="w-4 h-4 ml-2" />
										</button>

										{showAddGoalDropdown && (
											<div className="dropdown-menu">
												<button
													onClick={() => handleAddGoal('pageview')}
													className="dropdown-item"
												>
													<span className="dropdown-icon">+</span>
													Pageview
												</button>
												<button
													onClick={() => handleAddGoal('custom_event')}
													className="dropdown-item"
												>
													<span className="dropdown-icon">+</span>
													Custom event
												</button>
												<button
													onClick={() => handleAddGoal('scroll_depth')}
													className="dropdown-item"
												>
													<span className="dropdown-icon">+</span>
													Scroll depth
												</button>
											</div>
										)}
									</div>
								</div>

								{/* Goals Table */}
								<div className="goals-table">
									<div className="goals-table-header">
										<div className="goals-table-col-name">Name</div>
										<div className="goals-table-col-type">Type</div>
										<div className="goals-table-col-actions"></div>
									</div>

									{goals.filter(goal => 
										goal.name.toLowerCase().includes(goalSearchQuery.toLowerCase())
									).map((goal) => (
										<div key={goal.id} className="goals-table-row">
											<div className="goals-table-col-name">
												<span className="goal-name">{goal.name}</span>
											</div>
											<div className="goals-table-col-type">
												<span className="goal-type-badge">
													{goal.goal_type === 'custom_event' ? 'Custom Event' : 
													 goal.goal_type === 'pageview' ? 'Pageview' : 
													 goal.goal_type === 'scroll_depth' ? 'Scroll Depth' : 
													 goal.goal_type}
												</span>
											</div>
											<div className="goals-table-col-actions">
												<button 
													onClick={() => handleEditGoal(goal)}
													className="action-button edit-button" 
													title="Edit goal"
												>
													<Edit2 className="w-4 h-4" />
												</button>
												<button
													onClick={() => handleDeleteGoal(goal.id)}
													className="action-button delete-button"
													title="Delete goal"
												>
													<Trash2 className="w-4 h-4" />
												</button>
											</div>
										</div>
									))}

									{goals.length === 0 && (
										<div className="goals-empty">
											<p>No goals created yet. Click "Add goal" to create one.</p>
										</div>
									)}
								</div>
							</div>
						</div>
					)}

					{/* Funnels Tab */}
					{activeTab === 'funnels' && (
						<div className="settings-content">
							<div className="settings-section">
								<div className="section-header">
									<h2 className="section-title">Funnels</h2>
								</div>
								
								{/* Show in Dashboard Toggle */}
								<div className="settings-option">
									<div className="option-row">
										<label className="option-label">Show in dashboard</label>
										<label className="toggle-switch">
											<input
												type="checkbox"
												checked={funnelShowInDashboard}
												onChange={(e) => setFunnelShowInDashboard(e.target.checked)}
											/>
											<span className="toggle-slider"></span>
										</label>
									</div>
								</div>

								{/* Empty State or Funnels List */}
								{funnels.length === 0 ? (
									<div className="empty-state mt-8 p-8 border-2 border-dashed border-gray-200 rounded-lg text-center">
										<FilterIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
										<h3 className="text-lg font-medium text-gray-900 mb-2">Create your first funnel</h3>
										<p className="text-gray-500 mb-6 max-w-md mx-auto">
											Funnels let you track conversion rates across a series of steps. For example, you can create a funnel to track how many visitors go from your homepage to signing up.
										</p>
										<Button 
											onClick={() => setShowFunnelModal(true)}
											className="bg-indigo-600 hover:bg-indigo-700 text-white"
										>
											<Plus className="w-4 h-4 mr-2" />
											Add funnel
										</Button>
									</div>
								) : (
									<>
										{/* Add Funnel Button */}
										<div className="flex justify-end mt-4">
											<Button 
												onClick={() => setShowFunnelModal(true)}
												className="bg-indigo-600 hover:bg-indigo-700 text-white"
											>
												<Plus className="w-4 h-4 mr-2" />
												Add funnel
											</Button>
										</div>

										{/* Funnels List */}
										<div className="mt-4 space-y-3">
											{funnels.map((funnel) => (
												<div 
													key={funnel.id} 
													className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
												>
													<div className="flex-1">
														<h4 className="font-medium text-gray-900">{funnel.name}</h4>
														<p className="text-sm text-gray-500 mt-1">
															{funnel.steps?.length || 0} steps: {funnel.steps?.map(s => s.page_path).join(' → ')}
														</p>
													</div>
													<div className="flex items-center gap-2">
														<button
															onClick={() => handleEditFunnel(funnel)}
															className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
															title="Edit funnel"
														>
															<Edit2 className="w-4 h-4" />
														</button>
														<button
															onClick={() => handleDeleteFunnel(funnel.id)}
															className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
															title="Delete funnel"
														>
															<Trash2 className="w-4 h-4" />
														</button>
													</div>
												</div>
											))}
										</div>
									</>
								)}
							</div>
						</div>
					)}

					{/* Danger Zone Tab */}
					{activeTab === 'danger' && (
						<div className="settings-content">
							<div className="settings-section">
								<h2 className="section-title text-red-600">Danger zone</h2>
								
								{/* Warning Banner */}
								<div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
									<div className="flex items-start gap-3">
										<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
										<p className="text-red-700 text-sm">
											Destructive actions below can result in irrecoverable data loss. Be careful.
										</p>
									</div>
								</div>

								{/* Transfer Site Ownership */}
								<div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
									<div className="flex items-start justify-between gap-4">
										<div className="flex-1">
											<h3 className="font-medium text-gray-900 mb-1">Transfer site ownership</h3>
											<p className="text-sm text-gray-500">
												Transfer ownership of the site to a different account. The new owner must have an existing account.
											</p>
										</div>
										<Button
											variant="outline"
											className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 whitespace-nowrap"
											onClick={() => setShowTransferModal(true)}
										>
											Transfer {website?.domain?.replace(/^https?:\/\//, '').replace(/^www\./, '')} ownership
										</Button>
									</div>
								</div>

								{/* Reset Stats */}
								<div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
									<div className="flex items-start justify-between gap-4">
										<div className="flex-1">
											<h3 className="font-medium text-gray-900 mb-1">Reset stats</h3>
											<p className="text-sm text-gray-500">
												Reset all statistics but keep the site configuration intact. Goals, funnels, and settings will be preserved.
											</p>
										</div>
										<Button
											variant="outline"
											className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 whitespace-nowrap"
											onClick={() => setShowResetModal(true)}
										>
											Reset {website?.domain?.replace(/^https?:\/\//, '').replace(/^www\./, '')} stats
										</Button>
									</div>
								</div>

								{/* Delete Site */}
								<div className="bg-white border border-gray-200 rounded-lg p-6">
									<div className="flex items-start justify-between gap-4">
										<div className="flex-1">
											<h3 className="font-medium text-gray-900 mb-1">Delete site</h3>
											<p className="text-sm text-gray-500">
												Permanently delete all stats and site settings. This action cannot be undone.
											</p>
										</div>
										<Button
											variant="outline"
											className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 whitespace-nowrap"
											onClick={() => setShowDeleteModal(true)}
										>
											Delete {website?.domain?.replace(/^https?:\/\//, '').replace(/^www\./, '')}
										</Button>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Email Reports Tab */}
					{activeTab === 'email' && (
						<div className="settings-content">
							<div className="space-y-6">
								{/* Weekly Email Reports */}
								<div className="bg-white border border-gray-200 rounded-lg p-6">
									<div className="flex items-center justify-between mb-2">
										<h2 className="text-xl font-semibold text-gray-900">Weekly email reports</h2>
										<button className="text-gray-400 hover:text-gray-600">
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										</button>
									</div>
									<p className="text-sm text-gray-500 mb-4">Send weekly analytics reports to as many addresses as you wish.</p>
									
									<div className="flex items-center gap-3 mb-4">
										<label className="toggle-switch">
											<input
												type="checkbox"
												checked={emailSettings.weekly_report?.enabled || false}
												onChange={() => toggleReport('weekly_report')}
											/>
											<span className="toggle-slider"></span>
										</label>
										<span className="text-sm text-gray-700">Send a weekly email report every Monday</span>
									</div>

									<div>
										<h3 className="text-sm font-medium text-gray-900 mb-3">Recipients</h3>
										<div className="space-y-2">
											{emailSettings.weekly_report?.recipients?.map((email, idx) => (
												<div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
													<div className="flex items-center gap-2">
														<Mail className="w-4 h-4 text-gray-400" />
														<span className="text-sm text-gray-700">{email}</span>
													</div>
													<button
														onClick={() => removeRecipient('weekly', email)}
														className="text-red-500 hover:text-red-700"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											))}
										</div>
										
										<div className="flex gap-2 mt-3">
											<Input
												type="email"
												placeholder="e.g. joe@example.com"
												value={newRecipient.weekly}
												onChange={(e) => setNewRecipient({ ...newRecipient, weekly: e.target.value })}
												className="flex-1"
											/>
											<Button onClick={() => addRecipient('weekly')} className="bg-indigo-600 hover:bg-indigo-700">
												Add recipient
											</Button>
										</div>
									</div>
								</div>

								{/* Monthly Email Reports */}
								<div className="bg-white border border-gray-200 rounded-lg p-6">
									<div className="flex items-center justify-between mb-2">
										<h2 className="text-xl font-semibold text-gray-900">Monthly email reports</h2>
										<button className="text-gray-400 hover:text-gray-600">
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										</button>
									</div>
									<p className="text-sm text-gray-500 mb-4">Send monthly analytics reports to as many addresses as you wish.</p>
									
									<div className="flex items-center gap-3 mb-4">
										<label className="toggle-switch">
											<input
												type="checkbox"
												checked={emailSettings.monthly_report?.enabled || false}
												onChange={() => toggleReport('monthly_report')}
											/>
											<span className="toggle-slider"></span>
										</label>
										<span className="text-sm text-gray-700">Send a monthly email report on 1st of the month</span>
									</div>

									<div>
										<h3 className="text-sm font-medium text-gray-900 mb-3">Recipients</h3>
										<div className="space-y-2">
											{emailSettings.monthly_report?.recipients?.map((email, idx) => (
												<div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
													<div className="flex items-center gap-2">
														<Mail className="w-4 h-4 text-gray-400" />
														<span className="text-sm text-gray-700">{email}</span>
													</div>
													<button
														onClick={() => removeRecipient('monthly', email)}
														className="text-red-500 hover:text-red-700"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											))}
										</div>
										
										<div className="flex gap-2 mt-3">
											<Input
												type="email"
												placeholder="e.g. joe@example.com"
												value={newRecipient.monthly}
												onChange={(e) => setNewRecipient({ ...newRecipient, monthly: e.target.value })}
												className="flex-1"
											/>
											<Button onClick={() => addRecipient('monthly')} className="bg-indigo-600 hover:bg-indigo-700">
												Add recipient
											</Button>
										</div>
									</div>
								</div>

								{/* Traffic Spike Notifications */}
								<div className="bg-white border border-gray-200 rounded-lg p-6">
									<div className="flex items-center justify-between mb-2">
										<h2 className="text-xl font-semibold text-gray-900">Traffic spike notifications</h2>
										<button className="text-gray-400 hover:text-gray-600">
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										</button>
									</div>
									<p className="text-sm text-gray-500 mb-4">Get notified when your site has unusually high number of current visitors.</p>
									
									<div className="flex items-center gap-3 mb-6">
										<label className="toggle-switch">
											<input
												type="checkbox"
												checked={emailSettings.traffic_spike?.enabled || false}
												onChange={() => toggleReport('traffic_spike')}
											/>
											<span className="toggle-slider"></span>
										</label>
										<span className="text-sm text-gray-700">Send notifications of traffic spikes</span>
									</div>

									<div className="mb-6">
										<h3 className="text-sm font-medium text-gray-900 mb-2">Current visitors threshold</h3>
										<div className="flex gap-2">
											<Input
												type="number"
												value={spikeThreshold}
												onChange={(e) => setSpikeThreshold(parseInt(e.target.value) || 10)}
												className="w-40"
												min="1"
											/>
											<Button onClick={() => saveThreshold('spike')} className="bg-indigo-600 hover:bg-indigo-700">
												Save threshold
											</Button>
										</div>
									</div>

									<div>
										<h3 className="text-sm font-medium text-gray-900 mb-3">Recipients</h3>
										<div className="space-y-2">
											{emailSettings.traffic_spike?.recipients?.map((email, idx) => (
												<div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
													<div className="flex items-center gap-2">
														<Mail className="w-4 h-4 text-gray-400" />
														<span className="text-sm text-gray-700">{email}</span>
													</div>
													<button
														onClick={() => removeRecipient('spike', email)}
														className="text-red-500 hover:text-red-700"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											))}
										</div>
										
										<div className="flex gap-2 mt-3">
											<Input
												type="email"
												placeholder="e.g. joe@example.com"
												value={newRecipient.spike}
												onChange={(e) => setNewRecipient({ ...newRecipient, spike: e.target.value })}
												className="flex-1"
											/>
											<Button onClick={() => addRecipient('spike')} className="bg-indigo-600 hover:bg-indigo-700">
												Add recipient
											</Button>
										</div>
									</div>
								</div>

								{/* Traffic Drop Notifications */}
								<div className="bg-white border border-gray-200 rounded-lg p-6">
									<div className="flex items-center justify-between mb-2">
										<h2 className="text-xl font-semibold text-gray-900">Traffic drop notifications</h2>
										<button className="text-gray-400 hover:text-gray-600">
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										</button>
									</div>
									<p className="text-sm text-gray-500 mb-4">Get notified when your site has unusually low number of visitors within 12 hours.</p>
									
									<div className="flex items-center gap-3 mb-6">
										<label className="toggle-switch">
											<input
												type="checkbox"
												checked={emailSettings.traffic_drop?.enabled || false}
												onChange={() => toggleReport('traffic_drop')}
											/>
											<span className="toggle-slider"></span>
										</label>
										<span className="text-sm text-gray-700">Send notifications of traffic drops</span>
									</div>

									<div className="mb-6">
										<h3 className="text-sm font-medium text-gray-900 mb-2">12 hours visitor threshold</h3>
										<div className="flex gap-2">
											<Input
												type="number"
												value={dropThreshold}
												onChange={(e) => setDropThreshold(parseInt(e.target.value) || 1)}
												className="w-40"
												min="0"
											/>
											<Button onClick={() => saveThreshold('drop')} className="bg-indigo-600 hover:bg-indigo-700">
												Save threshold
											</Button>
										</div>
									</div>

									<div>
										<h3 className="text-sm font-medium text-gray-900 mb-3">Recipients</h3>
										<div className="space-y-2">
											{emailSettings.traffic_drop?.recipients?.map((email, idx) => (
												<div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
													<div className="flex items-center gap-2">
														<Mail className="w-4 h-4 text-gray-400" />
														<span className="text-sm text-gray-700">{email}</span>
													</div>
													<button
														onClick={() => removeRecipient('drop', email)}
														className="text-red-500 hover:text-red-700"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											))}
										</div>
										
										<div className="flex gap-2 mt-3">
											<Input
												type="email"
												placeholder="e.g. joe@example.com"
												value={newRecipient.drop}
												onChange={(e) => setNewRecipient({ ...newRecipient, drop: e.target.value })}
												className="flex-1"
											/>
											<Button onClick={() => addRecipient('drop')} className="bg-indigo-600 hover:bg-indigo-700">
												Add recipient
											</Button>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Email Reports Tab */}
					{activeTab === 'people' && (

						
						<div className="settings-content">

							<div className="goals-section">
								{/* Header */}
								<div className="goals-header">
									<header className="goals-header-left">
										<h2 className="goals-title">People</h2>
										<button className="info-button">
											<svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										</button>
									</header>
								</div>

								{/* Description */}
								<p className="goals-description">
									Define actions that you want your users to take, like visiting a certain page, submitting a form, etc. You can also{' '}
									<a href="#" className="goals-link">compose goals into funnels</a>.
								</p>

								<Link to="/teams" className="rounded-md text-sm font-medium transition-colors focus-visible:ring-1 px-4 py-2 form-button">Invite new guest
								</Link>
								
								
								<div class="rounded-md p-5 relative bg-gray-100 dark:bg-gray-800 mt-4">
									<div class="w-full flex flex-col gap-y-1.5">
										<div class="text-sm text-gray-600 dark:text-gray-300 leading-5">

											<p>
												Team members automatically have access to this site.
												<a href="/settings/team/general"
													class="text-indigo-600 hover:text-indigo-700 dark:text-indigo-500 dark:hover:text-indigo-400 transition-colors duration-150 ">
													View team members
												</a>
											</p>
											
										</div>
									</div>
								</div>
								<div class="flow-root">
									<ul class="divide-y divide-gray-200 dark:divide-gray-700">
										<li class="py-4" id="membership-58833">
											<div class="flex items-center space-x-4">
												<div class="shrink-0">
													<div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-medium">
														{user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
													</div>
												</div>
												<div class="flex-1 min-w-0">
													<p class="text-sm truncate">
														<span class="font-medium text-gray-900 dark:text-gray-50">
															{user?.name || 'User'}
														</span>
														<br />
														<span class="text-gray-500 dark:text-gray-400">
															{user?.email || ''}
														</span>
													</p>
												</div>
												<div data-ui-state="disabled"
													class=" block rounded-md text-sm/6 text-gray-900 ui-disabled:text-gray-500 dark:text-gray-100 dark:ui-disabled:text-gray-400 px-3 py-1.5">
													<div>Owner</div>
													<div class="text-gray-500 dark:text-gray-400 text-xs/5">
														Site owner cannot be assigned to any other role
													</div>
												</div>
											</div>
										</li>
									</ul>
								</div>

							</div>

						
						</div>
					)}

					{/* IP Addresses Blocking Tab */}
					{activeTab === 'ip' && (
						<div className="settings-content">
							<div className="bg-white border border-gray-200 rounded-lg p-6">
								<div className="flex items-center justify-between mb-2">
									<h2 className="text-xl font-semibold text-gray-900">Block IP Addresses</h2>
									<button className="text-gray-400 hover:text-gray-600">
										<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
									</button>
								</div>
								<p className="text-sm text-gray-500 mb-4">
									Block specific IP addresses from being tracked. Enter IPv4 addresses (e.g., 192.168.1.1).
								</p>

								<div>
									<h3 className="text-sm font-medium text-gray-900 mb-3">Blocked IP Addresses</h3>
									<div className="space-y-2">
										{blockSettings.blocked_ips?.length === 0 ? (
											<p className="text-sm text-gray-500 italic py-4">No IP addresses blocked yet</p>
										) : (
											blockSettings.blocked_ips?.map((ip, idx) => (
												<div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
													<div className="flex items-center gap-2">
														<Globe className="w-4 h-4 text-gray-400" />
														<span className="text-sm text-gray-700 font-mono">{ip}</span>
													</div>
													<button
														onClick={() => removeBlockItem('ip', ip)}
														className="text-red-500 hover:text-red-700"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											))
										)}
									</div>

									<div className="flex gap-2 mt-4">
										<Input
											type="text"
											placeholder="e.g. 192.168.1.1"
											value={newBlockItem.ip}
											onChange={(e) => setNewBlockItem({ ...newBlockItem, ip: e.target.value })}
											className="flex-1 font-mono"
										/>
										<Button onClick={() => addBlockItem('ip')} className="bg-indigo-600 hover:bg-indigo-700">
											Block IP
										</Button>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Countries Blocking Tab */}
					{activeTab === 'countries' && (
						<div className="settings-content">
							<div className="bg-white border border-gray-200 rounded-lg p-6">
								<div className="flex items-center justify-between mb-2">
									<h2 className="text-xl font-semibold text-gray-900">Block Countries</h2>
									<button className="text-gray-400 hover:text-gray-600">
										<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
									</button>
								</div>
								<p className="text-sm text-gray-500 mb-4">
									Block traffic from specific countries.
								</p>

								<div>
									<h3 className="text-sm font-medium text-gray-900 mb-3">Blocked Countries</h3>
									<div className="space-y-2">
										{blockSettings.blocked_countries?.length === 0 ? (
											<p className="text-sm text-gray-500 italic py-4">No countries blocked yet</p>
										) : (
											blockSettings.blocked_countries?.map((countryCode, idx) => {
												const country = countries.find(c => c.code === countryCode);
												return (
													<div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
														<div className="flex items-center gap-2">
															<MapPin className="w-4 h-4 text-gray-400" />
															<span className="text-sm text-gray-700">
																<span className="font-semibold">{countryCode}</span> - {country?.name || countryCode}
															</span>
														</div>
														<button
															onClick={() => removeBlockItem('country', countryCode)}
															className="text-red-500 hover:text-red-700"
														>
															<Trash2 className="w-4 h-4" />
														</button>
													</div>
												);
											})
										)}
									</div>

									<div className="flex gap-2 mt-4">
										<Select
											value={newBlockItem.country ? { value: newBlockItem.country, label: `${newBlockItem.country} - ${countries.find(c => c.code === newBlockItem.country)?.name || newBlockItem.country}` } : null}
											onChange={(option) => setNewBlockItem({ ...newBlockItem, country: option ? option.value : '' })}
											options={countries.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
											placeholder="Select a country..."
											isClearable
											isSearchable
											className="flex-1"
											styles={{
												control: (base) => ({
													...base,
													minHeight: '42px',
													borderColor: '#d1d5db',
													'&:hover': {
														borderColor: '#9ca3af'
													}
												}),
												menu: (base) => ({
													...base,
													zIndex: 9999
												})
											}}
										/>
										<Button 
											onClick={() => addBlockItem('country')} 
											className="bg-indigo-600 hover:bg-indigo-700"
											disabled={!newBlockItem.country}
										>
											Block Country
										</Button>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Pages Blocking Tab */}
					{activeTab === 'pages' && (
						<div className="settings-content">
							<div className="bg-white border border-gray-200 rounded-lg p-6">
								<div className="flex items-center justify-between mb-2">
									<h2 className="text-xl font-semibold text-gray-900">Block Pages</h2>
									<button className="text-gray-400 hover:text-gray-600">
										<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
									</button>
								</div>
								<p className="text-sm text-gray-500 mb-4">
									Block specific pages from being tracked.
								</p>

								<div>
									<h3 className="text-sm font-medium text-gray-900 mb-3">Blocked Pages</h3>
									<div className="space-y-2">
										{blockSettings.blocked_pages?.length === 0 ? (
											<p className="text-sm text-gray-500 italic py-4">No pages blocked yet</p>
										) : (
											blockSettings.blocked_pages?.map((page, idx) => (
												<div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
													<div className="flex items-center gap-2">
														<FileText className="w-4 h-4 text-gray-400" />
														<span className="text-sm text-gray-700 font-mono">{page}</span>
													</div>
													<button
														onClick={() => removeBlockItem('page', page)}
														className="text-red-500 hover:text-red-700"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											))
										)}
									</div>

									<div className="flex gap-2 mt-4">
										<Select
											value={newBlockItem.page ? { value: newBlockItem.page, label: newBlockItem.page } : null}
											onChange={(option) => setNewBlockItem({ ...newBlockItem, page: option ? option.value : '' })}
											options={availablePages.map(p => ({ value: p, label: p }))}
											placeholder="Select a page..."
											isClearable
											isSearchable
											className="flex-1"
											styles={{
												control: (base) => ({
													...base,
													minHeight: '42px',
													borderColor: '#d1d5db',
													fontFamily: 'monospace',
													'&:hover': {
														borderColor: '#9ca3af'
													}
												}),
												menu: (base) => ({
													...base,
													zIndex: 9999
												}),
												option: (base) => ({
													...base,
													fontFamily: 'monospace'
												}),
												singleValue: (base) => ({
													...base,
													fontFamily: 'monospace'
												})
											}}
										/>
										<Button 
											onClick={() => addBlockItem('page')} 
											className="bg-indigo-600 hover:bg-indigo-700"
											disabled={!newBlockItem.page}
										>
											Block Page
										</Button>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Custom Properties Tab */}
					{activeTab === 'properties' && (
						<div className="settings-content">
							<div className="settings-section">
								<div className="section-header">
									<div className="flex items-center gap-2">
										<h2 className="section-title">Custom properties</h2>
										<button className="text-gray-400 hover:text-gray-600">
											<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										</button>
									</div>
								</div>
								
								<p className="text-sm text-gray-600 mb-6">
									Attach custom properties when sending a pageview or event to create custom metrics.
								</p>

								{/* Show in Dashboard Toggle */}
								<div className="flex items-center justify-between mb-6 p-3 bg-gray-50 rounded-lg">
									<span className="text-sm font-medium text-gray-700">Show in dashboard</span>
									<button
										onClick={() => setPropertyShowInDashboard(!propertyShowInDashboard)}
										className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
											propertyShowInDashboard ? 'bg-indigo-600' : 'bg-gray-300'
										}`}
									>
										<span
											className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
												propertyShowInDashboard ? 'translate-x-6' : 'translate-x-1'
											}`}
										/>
									</button>
								</div>

								{/* Properties List Section */}
								{customProperties.length === 0 ? (
									/* Empty State - Create Property */
									<div className="border border-gray-200 rounded-lg p-6">
										<h3 className="text-base font-semibold text-gray-900 mb-2">Create a custom property</h3>
										<p className="text-sm text-gray-600 mb-4">
											Attach custom properties when sending a pageview or event to create custom metrics.
										</p>
										<Button
											onClick={() => setShowPropertyModal(true)}
											className="bg-indigo-600 hover:bg-indigo-700 text-white"
										>
											<Plus className="w-4 h-4 mr-2" />
											Add property
										</Button>
									</div>
								) : (
									/* Properties List */
									<div className="space-y-4">
										{/* Search and Add Button Row */}
										<div className="flex items-center gap-4">
											<div className="relative flex-1">
												<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
												<Input
													type="text"
													placeholder="Press / to search"
													value={propertySearchQuery}
													onChange={(e) => setPropertySearchQuery(e.target.value)}
													className="pl-10 bg-gray-50 border-gray-200"
												/>
											</div>
											<Button
												onClick={() => setShowPropertyModal(true)}
												className="bg-indigo-600 hover:bg-indigo-700 text-white"
											>
												<Plus className="w-4 h-4 mr-2" />
												Add property
											</Button>
										</div>

										{/* Properties Table */}
										<div className="border border-gray-200 rounded-lg overflow-hidden">
											{filteredProperties.map((property, index) => (
												<div
													key={property.id}
													className={`flex items-center justify-between p-4 ${
														index !== filteredProperties.length - 1 ? 'border-b border-gray-200' : ''
													}`}
												>
													<span className="text-sm font-medium text-gray-900">{property.name}</span>
													<button
														onClick={() => deleteCustomProperty(property.id)}
														className="text-gray-400 hover:text-red-500 transition-colors"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											))}
											{filteredProperties.length === 0 && propertySearchQuery && (
												<div className="p-4 text-center text-sm text-gray-500">
													No properties found matching "{propertySearchQuery}"
												</div>
											)}
										</div>
									</div>
								)}
							</div>
						</div>
					)}

					{/* Visibility Tab */}
					{activeTab === 'visibility' && (
						<div className="settings-content">
							<div className="settings-section">
								{/* Public Dashboard Section */}
								<div className="mb-8">
									<div className="section-header">
										<div className="flex items-center gap-2">
											<h2 className="section-title">Public dashboard</h2>
											<button className="text-gray-400 hover:text-gray-600">
												<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
												</svg>
											</button>
										</div>
									</div>
									
									<p className="text-sm text-gray-600 mb-4">
										Make your stats publicly available at the following link:
									</p>
									
									<div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
										<div className="flex items-center gap-2 text-sm">
											<span className="text-gray-500">{window.location.origin}/site/</span>
											<span className="text-gray-900 font-medium">{website?.domain?.replace(/^https?:\/\//, '').replace(/^www\./, '')}</span>
										</div>
									</div>
									
									<div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
										<div>
											<p className="font-medium text-gray-900">Make stats publicly accessible</p>
											<p className="text-sm text-gray-500">Anyone with the link can view your analytics</p>
										</div>
										<button
											onClick={togglePublicVisibility}
											disabled={visibilityLoading}
											className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
												isPublic ? 'bg-indigo-600' : 'bg-gray-300'
											} ${visibilityLoading ? 'opacity-50' : ''}`}
										>
											<span
												className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
													isPublic ? 'translate-x-6' : 'translate-x-1'
												}`}
											/>
										</button>
									</div>

									{/* URL sanitization — only relevant when the dashboard is public.
									    Replaces website IDs / tokens in page URLs with generic labels
									    (e.g. /analytics/:website) so a public demo doesn't leak them. */}
									{isPublic && (
										<div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg mt-3">
											<div className="pr-4">
												<p className="font-medium text-gray-900">Hide sensitive URL details</p>
												<p className="text-sm text-gray-500">
													Replaces website IDs and tokens in page URLs with generic labels
													(e.g. <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">/analytics/:website</code>) in the public view.
												</p>
											</div>
											<button
												onClick={toggleSanitizeUrls}
												disabled={sanitizeLoading}
												className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
													sanitizeUrls ? 'bg-indigo-600' : 'bg-gray-300'
												} ${sanitizeLoading ? 'opacity-50' : ''}`}
											>
												<span
													className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
														sanitizeUrls ? 'translate-x-6' : 'translate-x-1'
													}`}
												/>
											</button>
										</div>
									)}
								</div>
								
								{/* Shared Links Section */}
								<div>
									<div className="section-header">
										<div className="flex items-center gap-2">
											<h2 className="section-title">Shared links</h2>
											<button className="text-gray-400 hover:text-gray-600">
												<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
												</svg>
											</button>
										</div>
									</div>
									
									<p className="text-sm text-gray-600 mb-4">
										You can also share your stats privately by generating a shared link. The links are impossible to guess and you can add password protection for extra security.
									</p>
									
									{sharedLinks.length === 0 ? (
										<div className="border border-gray-200 rounded-lg p-6 text-center">
											<p className="text-gray-500 mb-4">No shared links created yet</p>
											<Button
												onClick={() => setShowAddLinkModal(true)}
												className="bg-indigo-600 hover:bg-indigo-700 text-white"
											>
												<Plus className="w-4 h-4 mr-2" />
												+ New link
											</Button>
										</div>
									) : (
										<div>
											<div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
												{sharedLinks.map((link, idx) => (
													<div
														key={link.id}
														className={`flex items-center justify-between p-4 ${
															idx !== sharedLinks.length - 1 ? 'border-b border-gray-200' : ''
														}`}
													>
														<div className="flex-1">
															<p className="font-medium text-gray-900">{link.name}</p>
															<div className="flex items-center gap-2 mt-1">
																<span className="text-sm text-gray-500">
																	{window.location.origin}/share/{link.slug}
																</span>
																{link.has_password && (
																	<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
																		🔒 Password
																	</span>
																)}
															</div>
														</div>
														<div className="flex items-center gap-2">
															<button
																onClick={() => {
																	navigator.clipboard.writeText(`${window.location.origin}/share/${link.slug}`);
																	toast.success('Link copied to clipboard');
																}}
																className="p-2 text-gray-400 hover:text-gray-600"
																title="Copy link"
															>
																<Copy className="w-4 h-4" />
															</button>
															<button
																onClick={() => deleteSharedLink(link.id)}
																className="p-2 text-gray-400 hover:text-red-500"
																title="Delete link"
															>
																<Trash2 className="w-4 h-4" />
															</button>
														</div>
													</div>
												))}
											</div>
											<Button
												onClick={() => setShowAddLinkModal(true)}
												className="bg-indigo-600 hover:bg-indigo-700 text-white"
											>
												<Plus className="w-4 h-4 mr-2" />
												+ New link
											</Button>
										</div>
									)}
								</div>
							</div>
						</div>
					)}

					{/* Imports & Exports Tab */}
					{activeTab === 'imports' && (
						<ImportExportSection
							websiteId={websiteId}
							token={token}
						/>
					)}

					{/* Integrations Tab */}
					{activeTab === 'integrations' && (
						<IntegrationsSection
							websiteId={websiteId}
							token={token}
						/>
					)}

					{/* Placeholder for other tabs */}
					{activeTab !== 'general' && activeTab !== 'people' && activeTab !== 'goals' && activeTab !== 'funnels' && activeTab !== 'danger' && activeTab !== 'email' && activeTab !== 'ip' && activeTab !== 'countries' && activeTab !== 'pages' && activeTab !== 'properties' && activeTab !== 'visibility' && activeTab !== 'imports' && activeTab !== 'integrations' && (
						<div className="settings-content">
							<div className="settings-placeholder">
								<h2 className="placeholder-title">
									{menuItems.find(item => item.id === activeTab)?.label}
								</h2>
								<p className="placeholder-text">This section is coming soon</p>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Goal Modal */}
			<GoalModal
				isOpen={showGoalModal}
				onClose={() => {
					setShowGoalModal(false);
					setEditingGoal(null);
				}}
				goalType={selectedGoalType}
				websiteId={websiteId}
				websiteDomain={website?.domain || ''}
				token={token}
				onSuccess={handleGoalSuccess}
				editGoal={editingGoal}
			/>

			{/* Funnel Modal */}
			<FunnelModal
				isOpen={showFunnelModal}
				onClose={() => {
					setShowFunnelModal(false);
					setEditingFunnel(null);
				}}
				websiteId={websiteId}
				token={token}
				onSuccess={handleFunnelSuccess}
				editFunnel={editingFunnel}
			/>

			{/* Add Property Modal */}
			{showPropertyModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">
							Add property for {website?.domain?.replace(/^https?:\/\//, '').replace(/^www\./, '')}
						</h3>
						
						<div className="mb-4">
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Property
							</label>
							<Input
								type="text"
								placeholder="Enter property name (e.g., login, purchase, signup)"
								value={newPropertyName}
								onChange={(e) => setNewPropertyName(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										e.preventDefault();
										addCustomProperty();
									}
								}}
								className="w-full"
								autoFocus
							/>
						</div>
						
						<div className="flex gap-3 justify-end">
							<Button
								variant="outline"
								onClick={() => {
									setShowPropertyModal(false);
									setNewPropertyName('');
								}}
							>
								Cancel
							</Button>
							<Button
								className="bg-indigo-600 hover:bg-indigo-700 text-white"
								onClick={addCustomProperty}
								disabled={!newPropertyName.trim()}
							>
								Add property
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* Add Shared Link Modal */}
			{showAddLinkModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">
							Create shared link
						</h3>
						
						<div className="mb-4">
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Link name
							</label>
							<Input
								type="text"
								placeholder="e.g., Marketing team, Client report"
								value={newLinkName}
								onChange={(e) => setNewLinkName(e.target.value)}
								className="w-full"
								autoFocus
							/>
						</div>
						
						<div className="mb-4">
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Password (optional)
							</label>
							<Input
								type="password"
								placeholder="Leave empty for no password"
								value={newLinkPassword}
								onChange={(e) => setNewLinkPassword(e.target.value)}
								className="w-full"
							/>
							<p className="text-xs text-gray-500 mt-1">
								Add a password for extra security
							</p>
						</div>
						
						<div className="flex gap-3 justify-end">
							<Button
								variant="outline"
								onClick={() => {
									setShowAddLinkModal(false);
									setNewLinkName('');
									setNewLinkPassword('');
								}}
							>
								Cancel
							</Button>
							<Button
								className="bg-indigo-600 hover:bg-indigo-700 text-white"
								onClick={createSharedLink}
								disabled={!newLinkName.trim()}
							>
								Create link
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* Transfer Ownership Modal */}
			{showTransferModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">Transfer site ownership</h3>
						<p className="text-sm text-gray-500 mb-4">
							Enter the email address of the account you want to transfer <strong>{website?.domain?.replace(/^https?:\/\//, '').replace(/^www\./, '')}</strong> to.
							The new owner must have an existing account.
						</p>
						<Input
							type="email"
							placeholder="Email address of new owner"
							value={transferEmail}
							onChange={(e) => setTransferEmail(e.target.value)}
							className="mb-4"
						/>
						<div className="flex gap-3 justify-end">
							<Button
								variant="outline"
								onClick={() => {
									setShowTransferModal(false);
									setTransferEmail('');
								}}
							>
								Cancel
							</Button>
							<Button
								className="bg-red-600 hover:bg-red-700 text-white"
								onClick={handleTransferOwnership}
								disabled={dangerLoading}
							>
								{dangerLoading ? 'Transferring...' : 'Transfer ownership'}
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* Reset Stats Modal */}
			{showResetModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">Reset statistics</h3>
						<p className="text-sm text-gray-500 mb-4">
							This will permanently delete all analytics data for <strong>{website?.domain?.replace(/^https?:\/\//, '').replace(/^www\./, '')}</strong>.
							Your goals, funnels, and settings will be preserved.
						</p>
						<p className="text-sm text-gray-700 mb-2">
							Type <strong>{website?.domain}</strong> to confirm:
						</p>
						<Input
							type="text"
							placeholder="Type domain to confirm"
							value={confirmText}
							onChange={(e) => setConfirmText(e.target.value)}
							className="mb-4"
						/>
						<div className="flex gap-3 justify-end">
							<Button
								variant="outline"
								onClick={() => {
									setShowResetModal(false);
									setConfirmText('');
								}}
							>
								Cancel
							</Button>
							<Button
								className="bg-red-600 hover:bg-red-700 text-white"
								onClick={handleResetStats}
								disabled={dangerLoading || confirmText !== website?.domain}
							>
								{dangerLoading ? 'Resetting...' : 'Reset all stats'}
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* Delete Site Modal */}
			{showDeleteModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
						<h3 className="text-lg font-semibold text-red-600 mb-4">Delete site permanently</h3>
						<p className="text-sm text-gray-500 mb-4">
							This will permanently delete <strong>{website?.domain?.replace(/^https?:\/\//, '').replace(/^www\./, '')}</strong> and all associated data including analytics, goals, and funnels. This action cannot be undone.
						</p>
						<p className="text-sm text-gray-700 mb-2">
							Type <strong>{website?.domain}</strong> to confirm:
						</p>
						<Input
							type="text"
							placeholder="Type domain to confirm"
							value={confirmText}
							onChange={(e) => setConfirmText(e.target.value)}
							className="mb-4"
						/>
						<div className="flex gap-3 justify-end">
							<Button
								variant="outline"
								onClick={() => {
									setShowDeleteModal(false);
									setConfirmText('');
								}}
							>
								Cancel
							</Button>
							<Button
								className="bg-red-600 hover:bg-red-700 text-white"
								onClick={handleDeleteSite}
								disabled={dangerLoading || confirmText !== website?.domain}
							>
								{dangerLoading ? 'Deleting...' : 'Delete site forever'}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default SettingsPage;
